import React, { useMemo } from "react";
import {
  CartesianGrid, Legend, Line, LineChart, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

// 水情歷線圖。
//
// 取代舊的 WaterLineChart，修掉幾個長年存在的問題：
//   * 單位寫 "bar"（從油壓機具複製過來的），實際是 m / m/s / cms
//   * Y 軸寫死 [-6, 6]，水位 2–7 m、流量可達數十 cms，整條線常被切掉
//   * tickFormatter 是 `value.toPrecision(2) + 1` —— 字串串接，刻度會多一個 1
//   * 參考線用油壓的上下限，水情該用警戒水位
//   * 時間手動減 8 小時，現在後端回帶時區的 ISO 字串，直接 new Date()
//
// 三個量值的尺度差很多（水位個位數、流量可能上百），所以水位與流速共用左軸，
// 流量獨立用右軸。

const COLORS = {
  water_level: "#1f77b4",
  average_velocity: "#2ca02c",
  discharge: "#d62728",
};

const ALERT_LINES = [
  { key: "water_level_three_height", label: "三級", stroke: "#f0ad4e" },
  { key: "water_level_two_height", label: "二級", stroke: "#f06b4e" },
  { key: "water_level_one_height", label: "一級", stroke: "#d9363e" },
];

/** 套用 function_11 的線性校正。a/b 缺值時視為 1/0（不校正）。 */
export function calibrate(raw, slope, intercept) {
  if (raw === null || raw === undefined) return null;
  const a = Number.isFinite(Number(slope)) ? Number(slope) : 1;
  const b = Number.isFinite(Number(intercept)) ? Number(intercept) : 0;
  const value = Number(raw) * a + b;
  return Number.isFinite(value) ? value : null;
}

function formatTime(value, span) {
  const date = new Date(value);
  if (span > 2 * 86400000) {
    return date.toLocaleDateString([], { month: "2-digit", day: "2-digit" });
  }
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function WaterChart({
  rows = [],
  station = {},
  height = 320,
  show = { water_level: true, average_velocity: true, discharge: true },
}) {
  const data = useMemo(
    () =>
      rows.map((row) => ({
        t: new Date(row.time).getTime(),
        water_level: calibrate(row.water_level, station.a, station.b),
        average_velocity: calibrate(row.average_velocity, station.ra, station.rb),
        discharge: calibrate(row.discharge, station.sa, station.sb),
        // 品質不佳的點照畫，但 tooltip 會標示 —— 直接丟掉會讓曲線看起來
        // 比實際完整，反而誤導。
        trusted: row.quality ? row.quality.trusted !== false : null,
      })),
    [rows, station.a, station.b, station.ra, station.rb, station.sa, station.sb],
  );

  const span = data.length > 1 ? data[data.length - 1].t - data[0].t : 0;

  const alerts = ALERT_LINES
    .map((line) => ({ ...line, value: Number(station[line.key]) }))
    .filter((line) => Number.isFinite(line.value) && line.value > 0);

  if (!data.length) {
    return (
      <div style={{ height, display: "flex", alignItems: "center",
                    justifyContent: "center", color: "var(--secondary-text)" }}>
        此期間沒有資料
      </div>
    );
  }

  const tooltipFormatter = (value, name, item) => {
    if (value === null || value === undefined) return ["—", name];
    const units = { 水位: "m", 平均表面流速: "m/s", 推估流量: "cms" };
    const suffix = item?.payload?.trusted === false ? "（低可信度）" : "";
    return [`${Number(value).toFixed(3)} ${units[name] || ""}${suffix}`, name];
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis
          dataKey="t"
          type="number"
          domain={["dataMin", "dataMax"]}
          scale="time"
          tickFormatter={(value) => formatTime(value, span)}
          minTickGap={40}
        />
        {/* 水位與流速共用左軸；兩者都是個位數，尺度相近 */}
        <YAxis
          yAxisId="left"
          type="number"
          domain={["auto", "auto"]}
          tickFormatter={(value) => Number(value).toFixed(1)}
          label={{ value: "水位 m / 流速 m·s⁻¹", angle: -90,
                   position: "insideLeft", style: { fontSize: 11 } }}
        />
        {show.discharge && (
          <YAxis
            yAxisId="right"
            orientation="right"
            type="number"
            domain={["auto", "auto"]}
            tickFormatter={(value) => Number(value).toFixed(0)}
            label={{ value: "流量 cms", angle: 90, position: "insideRight",
                     style: { fontSize: 11 } }}
          />
        )}

        {/* 警戒水位：來自 function_11 的三級警戒欄位 */}
        {show.water_level &&
          alerts.map((line) => (
            <ReferenceLine
              key={line.key}
              yAxisId="left"
              y={line.value}
              stroke={line.stroke}
              strokeDasharray="6 3"
              label={{ value: `${line.label}警戒 ${line.value}m`,
                       position: "insideTopRight", fontSize: 11,
                       fill: line.stroke }}
            />
          ))}

        <Tooltip
          labelFormatter={(value) => new Date(value).toLocaleString()}
          formatter={tooltipFormatter}
          contentStyle={{ fontSize: 12 }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />

        {show.water_level && (
          <Line yAxisId="left" type="monotone" dataKey="water_level" name="水位"
                stroke={COLORS.water_level} dot={false} connectNulls={false}
                animationDuration={300} />
        )}
        {show.average_velocity && (
          <Line yAxisId="left" type="monotone" dataKey="average_velocity"
                name="平均表面流速" stroke={COLORS.average_velocity} dot={false}
                connectNulls={false} animationDuration={300} />
        )}
        {show.discharge && (
          <Line yAxisId="right" type="monotone" dataKey="discharge" name="推估流量"
                stroke={COLORS.discharge} dot={false} connectNulls={false}
                animationDuration={300} />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}